import { useState, useCallback } from 'react';

export interface RetryConfig {
  maxAttempts?: number;
  delay?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: Error) => boolean;
}

export interface RetryState {
  isRetrying: boolean;
  attemptCount: number;
  lastError: Error | null;
}

/**
 * Hook for implementing retry logic with exponential backoff
 * Provides automatic and manual retry capabilities for async operations
 */
export const useRetry = (config: RetryConfig = {}) => {
  const {
    maxAttempts = 3,
    delay = 1000,
    backoffMultiplier = 2,
    retryCondition = () => true
  } = config;

  const [retryState, setRetryState] = useState<RetryState>({
    isRetrying: false,
    attemptCount: 0,
    lastError: null
  });

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const executeWithRetry = useCallback(async <T>(
    operation: () => Promise<T>
  ): Promise<T> => {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setRetryState({
        isRetrying: attempt > 1,
        attemptCount: attempt,
        lastError: null
      });

      try {
        const result = await operation();
        
        // Success - reset state
        setRetryState({
          isRetrying: false,
          attemptCount: 0,
          lastError: null
        });
        
        return result;
      } catch (error) {
        lastError = error as Error;
        
        setRetryState({
          isRetrying: true,
          attemptCount: attempt,
          lastError
        });

        // Check if we should retry this error
        if (!retryCondition(lastError)) {
          break;
        }

        // If this was the last attempt, don't wait
        if (attempt === maxAttempts) {
          break;
        }

        // Wait before retrying with exponential backoff
        const waitTime = delay * Math.pow(backoffMultiplier, attempt - 1);
        await sleep(waitTime);
      }
    }

    // All attempts failed
    setRetryState({
      isRetrying: false,
      attemptCount: maxAttempts,
      lastError
    });

    throw lastError || new Error('Operation failed after all retry attempts');
  }, [maxAttempts, delay, backoffMultiplier, retryCondition]);

  const reset = useCallback(() => {
    setRetryState({
      isRetrying: false,
      attemptCount: 0,
      lastError: null
    });
  }, []);

  return {
    executeWithRetry,
    retryState,
    reset,
    isRetrying: retryState.isRetrying,
    attemptCount: retryState.attemptCount,
    lastError: retryState.lastError
  };
};

/**
 * Determines if an error is retryable based on common patterns
 */
export const isRetryableError = (error: Error): boolean => {
  const message = error.message.toLowerCase();
  
  // Network errors that are typically retryable
  const retryablePatterns = [
    'network error',
    'timeout',
    'connection',
    'fetch',
    'econnreset',
    'enotfound',
    'econnrefused',
    '500',
    '502',
    '503',
    '504'
  ];

  return retryablePatterns.some(pattern => message.includes(pattern));
};

/**
 * Hook specifically for API operations with sensible retry defaults
 */
export const useApiRetry = () => {
  return useRetry({
    maxAttempts: 3,
    delay: 1000,
    backoffMultiplier: 2,
    retryCondition: isRetryableError
  });
};